import { useState } from "react";
import { ShieldCheck, Save } from "lucide-react";
import { toast } from "sonner";
import { usePrefs } from "@/hooks/useCollection";
import { rupiah, todayIso, fmtDate } from "@/lib/format";
import { calcJaminan } from "@/lib/jaminan";
import { Button } from "@/components/ui/button";
import { Section, Stat } from "./shared";
import { errMsg } from "@/lib/api";

export const Field = ({ label, hint, children }) => (
  <label className="text-xs font-medium text-pink-600 space-y-1 block">
    <span>{label}</span>
    {children}
    {hint && <span className="block text-[11px] font-normal text-gray-400">{hint}</span>}
  </label>
);

const EMPTY = { start: "", end: "", deposit: 0, deposit_monthly: 0, saving_monthly: 0 };

export default function JaminanTab() {
  const { prefs, save } = usePrefs();
  if (!prefs) return <div className="h-40 rounded-3xl bg-pink-100/60 animate-pulse" />;
  return <JaminanForm prefs={prefs} save={save} />;
}

function JaminanForm({ prefs, save }) {
  const [v, setV] = useState(() => ({ ...EMPTY, ...prefs.jaminan }));
  const [untilToday, setUntilToday] = useState(() => !prefs.jaminan?.end);

  const set = (k) => (e) => setV((p) => ({ ...p, [k]: e.target.value }));
  const cfg = { ...v, end: untilToday ? "" : v.end };
  const r = calcJaminan(cfg);


  const submit = async () => {
    try {
      await save({ jaminan: { start: cfg.start, end: cfg.end, deposit: Number(cfg.deposit) || 0, deposit_monthly: Number(cfg.deposit_monthly) || 0, saving_monthly: Number(cfg.saving_monthly) || 0 } });
      toast.success("Data uang jaminan disimpan");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="duitku-jaminan">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Masa kerjaku" value={r.label} testid="jaminan-period" />
        <Stat label="Uang jaminan" value={rupiah(r.deposit + r.depositMonthly)} tone="text-sky-500" testid="jaminan-deposit-total" />
        <Stat label="Tabungan potongan gaji" value={rupiah(r.saving)} tone="text-pink-500" testid="jaminan-saving-total" />
        <Stat label="Total uangku di kantor" value={rupiah(r.total)} tone="text-emerald-500" testid="jaminan-total" />
      </div>
      <Section title={<span className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-sky-400" /> Kalkulator uang jaminan & tabungan</span>}>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Mulai kerja"><input className="field" type="date" value={v.start} onChange={set("start")} data-testid="jaminan-start" /></Field>
          <Field label="Sampai">
            <div className="flex gap-2">
              <button type="button" onClick={() => setUntilToday(true)} className={`chip ${untilToday ? "chip-on" : "chip-off"}`} data-testid="jaminan-until-today">Hari ini</button>
              <button type="button" onClick={() => setUntilToday(false)} className={`chip ${!untilToday ? "chip-on" : "chip-off"}`} data-testid="jaminan-until-date">Pilih tanggal</button>
            </div>
            {!untilToday && <input className="field mt-2" type="date" value={v.end} onChange={set("end")} data-testid="jaminan-end" />}
          </Field>
          <Field label="Uang jaminan dibayar di awal (Rp)" hint="Uang yang kamu bayarkan ke kantor sebagai jaminan"><input className="field" type="number" min="0" value={v.deposit} onChange={set("deposit")} data-testid="jaminan-deposit" /></Field>
          <Field label="Cicilan jaminan per bulan (Rp)" hint="Isi 0 kalau jaminan dibayar sekali saja"><input className="field" type="number" min="0" value={v.deposit_monthly} onChange={set("deposit_monthly")} data-testid="jaminan-deposit-monthly" /></Field>
          <Field label="Tabungan dipotong dari gaji per bulan (Rp)"><input className="field" type="number" min="0" value={v.saving_monthly} onChange={set("saving_monthly")} data-testid="jaminan-saving-monthly" /></Field>
        </div>
        <div className="mt-5 rounded-2xl bg-sky-50/70 p-4 text-sm text-gray-600 space-y-1" data-testid="jaminan-breakdown">
          <p>Dihitung sampai <strong>{fmtDate(untilToday ? todayIso() : v.end)}</strong> · {r.months} bulan potongan ({r.days} hari kalender)</p>
          <p>Jaminan awal: {rupiah(r.deposit)}</p>
          <p>Cicilan jaminan: {r.months} x {rupiah(v.deposit_monthly)} = {rupiah(r.depositMonthly)}</p>
          <p>Tabungan potongan: {r.months} x {rupiah(v.saving_monthly)} = {rupiah(r.saving)}</p>
        </div>
        <Button onClick={submit} className="rounded-full h-11 mt-4" data-testid="jaminan-save"><Save className="w-4 h-4 mr-2" /> Simpan</Button>
      </Section>
    </div>
  );
}
